import React from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";
import { notification } from "antd";

import { I18nProvider } from "base/i18n";

import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { Link } from "../../navigation/link/index.jsx";

import "antd/es/notification/style/css";
import styles from "./toast.css";

export const ACTIONTOAST_TYPES = {
  NORMAL: "normal",
  ERRORS: "errors"
};

notification.config({
  placement: "topRight",
  className: styles.toast
});
const isUserAbortedRequest = (message) => {
  if (typeof message === "string") {
    return message?.includes("The user aborted a request");
  }
};
export function ToastFunctions() {
  return {
    showInfo: ({
      message,
      icon,
      onClick,
      onClose,
      showAction = false,
      actionMessage = "",
      onActionClicked = () => {}
    }) => {
      notification.config({
        rtl: window.isRTL
      });
      notification.info({
        title: "",
        description: (
          <I18nProvider>
            {showAction ? (
              <div className={styles.messageContainer}>
                <div>{message}</div>
                <div>
                  <Link onClick={onActionClicked}>{actionMessage}</Link>
                </div>
              </div>
            ) : (
              message
            )}
          </I18nProvider>
        ),
        top: 16,
        onClick,
        onClose: (e) => {
          notification.destroy();
          onClose && onClose(e);
        },
        className: `${styles.info} shadow-8dp`,
        icon: (
          <KfIcon
            icon={KF_ICONS.INFO_WITH_CIRCLE}
            size={ICON_SIZE.SIZE_20}
            stroke={ICON_STROKE.REGULAR}
            color="--kf-color-white"
          />
        ),
        closeIcon: (
          <KfIcon
            icon={KF_ICONS.CLOSE}
            size={ICON_SIZE.SIZE_12}
            stroke={ICON_STROKE.REGULAR}
          />
        )
      });
    },
    showSuccess: ({
      message,
      onClick,
      onClose,
      duration = 4.6,
      showAction = false,
      actionMessage,
      onActionClicked = () => {}
    }) => {
      notification.config({
        rtl: window.isRTL
      });
      notification.success({
        title: "",
        description: (
          <I18nProvider>
            {showAction ? (
              <div className={styles.messageContainer}>
                <div>{message}</div>
                <div>
                  <Link onClick={onActionClicked}>{actionMessage}</Link>
                </div>
              </div>
            ) : (
              message
            )}
          </I18nProvider>
        ),
        top: 16,
        onClick,
        onClose: (e) => {
          notification.destroy();
          onClose && onClose(e);
        },
        className: `${styles.success} shadow-8dp`,
        icon: (
          <KfIcon
            icon={KF_ICONS.CHECKMARK}
            size={ICON_SIZE.SIZE_20}
            stroke={ICON_STROKE.REGULAR}
            color="--kf-color-white"
          />
        ),
        closeIcon: (
          <KfIcon
            icon={KF_ICONS.CLOSE}
            size={ICON_SIZE.SIZE_12}
            stroke={ICON_STROKE.REGULAR}
          />
        ),
        duration: duration
      });
    },
    showWarning: ({
      message,
      icon,
      onClick,
      onClose,
      duration,
      showAction = false,
      actionMessage,
      onActionClicked
    }) => {
      notification.config({
        rtl: window.isRTL
      });
      notification.warning({
        title: "",
        description: (
          <I18nProvider>
            {showAction ? (
              <div className={styles.messageContainer}>
                <div>{message}</div>
                <div>
                  <Link onClick={onActionClicked}>{actionMessage}</Link>
                </div>
              </div>
            ) : (
              message
            )}
          </I18nProvider>
        ),
        top: 16,
        onClick,
        onClose: (e) => {
          notification.destroy();
          onClose && onClose(e);
        },
        className: `${styles.warning} shadow-8dp`,
        icon: (
          <KfIcon
            icon={KF_ICONS.WARNING}
            size={ICON_SIZE.SIZE_20}
            stroke={ICON_STROKE.REGULAR}
            color="--kf-color-white"
          />
        ),
        closeIcon: (
          <KfIcon
            icon={KF_ICONS.CLOSE}
            size={ICON_SIZE.SIZE_12}
            stroke={ICON_STROKE.REGULAR}
          />
        ),
        duration: duration
      });
    },
    showError: ({
      message,
      icon,
      onClick,
      onClose,
      showAction = false,
      actionMessage,
      onActionClicked = () => {}
    }) => {
      if (isUserAbortedRequest(message) || !message?.length) return;
      notification.config({
        rtl: window.isRTL
      });
      notification.error({
        title: "",
        description: (
          <I18nProvider>
            {showAction ? (
              <div className={styles.messageContainer}>
                <div>{message}</div>
                <div>
                  <Link onClick={onActionClicked}>{actionMessage}</Link>
                </div>
              </div>
            ) : (
              message
            )}
          </I18nProvider>
        ),
        duration: 0,
        top: 16,
        onClick,
        onClose: (e) => {
          notification.destroy();
          onClose && onClose(e);
        },
        className: `${styles.error} shadow-8dp`,
        icon: (
          <KfIcon
            icon={KF_ICONS.WARNING}
            size={ICON_SIZE.SIZE_20}
            stroke={ICON_STROKE.REGULAR}
            color="--kf-color-white"
          />
        ),
        closeIcon: (
          <KfIcon
            icon={KF_ICONS.CLOSE}
            size={ICON_SIZE.SIZE_12}
            stroke={ICON_STROKE.REGULAR}
          />
        )
      });
    }
  };
}

const ActionToastFunction = (props) => {
  const { content, onClose, type, className, cancelLabel } = props;
  return (
    <div
      className={`shadow-4dp ${className} ${styles.infoMsg} ${styles.actionToast} ${styles[type]}`}
    >
      <div className={`tiny ${className}  ${styles.sampleDataContentDetails}`}>
        {content}
      </div>
      <div
        className={`tiny-600 ${className}  ${styles.sampleDataDismiss}`}
        onClick={onClose}
      >
        {cancelLabel ? cancelLabel : <Trans>Dismiss</Trans>}
      </div>
    </div>
  );
};

ActionToastFunction.propTypes = {
  /**
   * It will render content of action toast
   */
  content: PropTypes.string,
  onClose: PropTypes.func,
  /**
   * it will render type of toast
   */
  type: PropTypes.string,
  className: PropTypes.string,
  /**
   * it will render dismiss label
   */
  cancelLabel: PropTypes.string
};

// eslint-disable-next-line kf/const-naming-convention
export const ActionToast = ActionToastFunction;

export const Toast = ToastFunctions();

// eslint-disable-next-line kf/const-naming-convention
export const PlainToast = notification;
